// Scheduled Reports management (Usage & Cost Tracking, Phase 4).
// CRUD against /api/usage/schedules. A schedule emails a PDF to each recipient,
// generated against THAT recipient's own live privileges (server-enforced) —
// recipients must be platform users; the backend rejects unknown emails.
import React, { useCallback, useEffect, useState } from 'react';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { authHeaders } from '../../utils/authHeader';
import './UsageDashboard.css';
import './ScheduledReports.css';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']; // Python Mon=0

const EMPTY_FORM = {
  report_def_id: '',      // which saved report to send (defined in the dashboard)
  frequencyType: 'daily',
  weekdays: [],
  monthdays: '',
  delivery_time: '06:00',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  recipients: '',
  enabled: true,
};

async function api(method, path, body) {
  const res = await fetch(path, {
    method,
    headers: await authHeaders({ 'Content-Type': 'application/json' }),
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data?.invalid_recipients
      ? ` (${data.invalid_recipients.join(', ')})` : '';
    throw new Error((data?.error || `HTTP ${res.status}`) + detail);
  }
  return data;
}

function describePeriod(sch, reports) {
  const rep = (reports || []).find((r) => r.id === sch.report_def_id);
  if (rep) return rep.range_mode.replace(/_/g, ' ');
  const f = sch.filters || {};
  if (f.date_from || f.date_to) {
    return `${(f.date_from || '…').slice(0, 10)} → ${(f.date_to || '…').slice(0, 10)}`;
  }
  return `Last ${f.preset || 'month'}`;
}

function describeFrequency(freq) {
  if (!freq) return '—';
  if (freq.type === 'daily') return 'Every day';
  if (freq.type === 'weekly') {
    return `Weekly: ${(freq.weekdays || []).map((d) => WEEKDAYS[d]).join(', ')}`;
  }
  if (freq.type === 'monthly') {
    return `Monthly: day ${(freq.monthdays || []).join(', ')}`;
  }
  return '—';
}

const ScheduledReports = () => {
  const { permissions, loading: permsLoading } = useUserPermissions();
  const perms = permissions?.permissions || [];
  const canSchedule = perms.includes('schedule_usage_reports');

  const [schedules, setSchedules] = useState([]);
  const [reports, setReports] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api('GET', '/api/usage/schedules');
      setSchedules(data.schedules || []);
    } catch (e) {
      setError(`Failed to load schedules: ${e.message}`);
    }
  }, []);

  useEffect(() => { if (canSchedule) load(); }, [canSchedule, load]);

  useEffect(() => {
    if (!canSchedule) return;
    api('GET', '/api/usage/reports')
      .then((d) => setReports(d.reports || []))
      .catch(() => {});
  }, [canSchedule]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const selectedReport = reports.find((r) => r.id === form.report_def_id) || null;
  const reportName = (sch) => {
    const hit = reports.find((r) => r.id === sch.report_def_id);
    if (hit) return hit.name;
    return sch.report_type === 'cost' ? 'Usage & Cost (legacy)' : 'Usage (legacy)';
  };

  const toggleWeekday = (d) => setForm((f) => ({
    ...f,
    weekdays: f.weekdays.includes(d)
      ? f.weekdays.filter((x) => x !== d)
      : [...f.weekdays, d].sort(),
  }));

  const startEdit = (sch) => {
    setEditingId(sch.id);
    setForm({
      report_def_id: sch.report_def_id || '',
      frequencyType: sch.frequency?.type || 'daily',
      weekdays: sch.frequency?.weekdays || [],
      monthdays: (sch.frequency?.monthdays || []).join(','),
      delivery_time: sch.delivery_time || '06:00',
      timezone: sch.timezone || 'UTC',
      recipients: (sch.recipients || []).join(', '),
      enabled: sch.enabled,
    });
    setNotice('');
    setError('');
  };

  const cancelEdit = () => { setEditingId(null); setForm(EMPTY_FORM); };

  const submit = async () => {
    if (!form.report_def_id) { setError('Choose a saved report to send'); return; }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const payload = {
        report_def_id: form.report_def_id,
        frequency: {
          type: form.frequencyType,
          weekdays: form.frequencyType === 'weekly' ? form.weekdays : [],
          monthdays: form.frequencyType === 'monthly'
            ? form.monthdays.split(',').map((x) => parseInt(x.trim(), 10)).filter((n) => !Number.isNaN(n))
            : [],
        },
        delivery_time: form.delivery_time,
        timezone: form.timezone,
        recipients: form.recipients.split(',').map((x) => x.trim()).filter(Boolean),
        enabled: form.enabled,
      };
      if (editingId) {
        await api('PUT', `/api/usage/schedules/${editingId}`, payload);
        setNotice('Schedule updated.');
      } else {
        await api('POST', '/api/usage/schedules', payload);
        setNotice('Schedule created.');
      }
      cancelEdit();
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const runNow = async (s) => {
    if (!window.confirm(
      `Send this report now to ${(s.recipients || []).join(', ')}?\n\n`
      + 'Each recipient gets a report scoped to their own access. '
      + 'This does not change the regular schedule.')) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await api('POST', `/api/usage/schedules/${s.id}/run-now`);
      setNotice(`Sent to ${res.sent} recipient(s)`
        + (res.skipped ? `; ${res.skipped} skipped (no access)` : ''));
      load();
    } catch (e) {
      setError(`Send failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete the schedule for "${reportName(s)}"?`)) return;
    try {
      await api('DELETE', `/api/usage/schedules/${s.id}`);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  if (permsLoading) {
    return (
      <div className="usage-dashboard">
        <div className="usage-content usage-state">
          <div className="usage-loading">
            <div className="usage-loading-spinner" aria-hidden="true" />
            <span>Loading…</span>
          </div>
        </div>
      </div>
    );
  }
  if (!canSchedule) {
    return (
      <div className="usage-dashboard">
        <div className="usage-content usage-state">
          <div className="usage-denied"><h2>Access Denied</h2>
            <p>You don't have permission to manage scheduled reports.</p></div>
        </div>
      </div>
    );
  }

  return (
    <div className="usage-dashboard">
      <main className="usage-content">
      <div className="usage-header"><h1>Scheduled Reports</h1></div>
      {error && <div className="usage-error">{error}</div>}
      {notice && <div className="sched-notice">{notice}</div>}

      <div className="sched-form">
        <h3>{editingId ? 'Edit schedule' : 'New schedule'}</h3>
        <div className="sched-grid">
          <label className="sched-wide">Report
            <select value={form.report_def_id}
              onChange={(e) => set('report_def_id', e.target.value)}>
              <option value="">— choose a saved report —</option>
              {reports.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} ({r.report_kind.replace(/_/g, ' ')} · {r.range_mode.replace(/_/g, ' ')})
                </option>
              ))}
            </select>
          </label>
          {reports.length === 0 && (
            <p className="sched-note sched-wide">
              No saved reports yet — open the Usage dashboard, set your filters,
              then use <b>Create report → Save as a report…</b>
            </p>
          )}
          {selectedReport && (
            <p className="sched-note sched-wide">
              Sends the <b>{selectedReport.range_mode.replace(/_/g, ' ')}</b> period each time,
              resolved at send: a monthly schedule on the 1st sends the whole previous month.
            </p>
          )}
          <label>Frequency
            <select value={form.frequencyType} onChange={(e) => set('frequencyType', e.target.value)}>
              <option value="daily">Every day</option>
              <option value="weekly">Selected weekdays</option>
              <option value="monthly">Selected month days</option>
            </select>
          </label>
          {form.frequencyType === 'weekly' && (
            <div className="sched-weekdays sched-wide">
              {WEEKDAYS.map((label, d) => (
                <button key={d} type="button"
                  className={form.weekdays.includes(d) ? 'active' : ''}
                  onClick={() => toggleWeekday(d)}>{label}</button>
              ))}
            </div>
          )}
          {form.frequencyType === 'monthly' && (
            <label className="sched-wide">Days of month (comma-separated, e.g. 1,15)
              <input type="text" value={form.monthdays}
                onChange={(e) => set('monthdays', e.target.value)} placeholder="1,15" />
            </label>
          )}
          <label>Delivery time
            <input type="time" value={form.delivery_time}
              onChange={(e) => set('delivery_time', e.target.value)} />
          </label>
          <label>Timezone
            <input type="text" value={form.timezone}
              onChange={(e) => set('timezone', e.target.value)} placeholder="UTC" />
          </label>
          <label className="sched-wide">Recipients (platform users, comma-separated)
            <input type="text" value={form.recipients}
              onChange={(e) => set('recipients', e.target.value)}
              placeholder="user1@company.com, user2@company.com" />
          </label>
          <label className="sched-inline"
            title="When off, the schedule is kept but no emails are sent (paused).">
            <input type="checkbox" checked={form.enabled}
              onChange={(e) => set('enabled', e.target.checked)} />
            Active (send on schedule; untick to pause without deleting)
          </label>
        </div>
        <p className="usage-muted sched-note">
          Each recipient receives a PDF scoped to their own current access —
          a recipient without cost access gets the usage version.
        </p>
        <div className="sched-actions">
          <button onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : editingId ? 'Save changes' : 'Create schedule'}
          </button>
          {editingId && <button className="sched-secondary" onClick={cancelEdit}>Cancel</button>}
        </div>
      </div>

      <div className="usage-table-wrap">
        <h3>My schedules</h3>
        <table className="usage-table">
          <thead>
            <tr>
              <th>Report</th><th>Period</th><th>Frequency</th><th>Time</th><th>Timezone</th>
              <th>Recipients</th><th>Active</th><th>Last sent</th><th>Last result</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {schedules.map((s) => (
              <tr key={s.id}>
                <td>{reportName(s)}</td>
                <td>{describePeriod(s, reports)}</td>
                <td>{describeFrequency(s.frequency)}</td>
                <td>{s.delivery_time}</td>
                <td>{s.timezone}</td>
                <td>{(s.recipients || []).join(', ')}</td>
                <td>{s.enabled ? 'Yes' : 'Paused'}</td>
                <td>{s.last_fired_at ? new Date(s.last_fired_at).toLocaleString() : '—'}</td>
                <td>
                  {s.last_status === 'error'
                    ? <span className="sched-status-error" title={s.last_error || ''}>Failed</span>
                    : s.last_status === 'ok'
                      ? <span className="sched-status-ok">Sent</span>
                      : <span className="usage-muted">—</span>}
                </td>
                <td>
                  <div className="actions">
                    <button onClick={() => runNow(s)} disabled={busy}>Send now</button>
                    <button onClick={() => startEdit(s)}>Edit</button>
                    <button className="btn-delete" onClick={() => remove(s)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {schedules.length === 0 && (
              <tr><td colSpan={10} className="usage-empty">No schedules yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      </main>
    </div>
  );
};

export default ScheduledReports;
